"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.KustoClient = void 0;
const moment_1 = __importDefault(require("moment"));
const uuid_1 = require("uuid");
const security_1 = __importDefault(require("./security"));
const response_1 = require("./response");
const connectionBuilder_1 = __importDefault(require("./connectionBuilder"));
const package_json_1 = __importDefault(require("../package.json"));
const axios_1 = __importDefault(require("axios"));
const http_1 = __importDefault(require("http"));
const https_1 = __importDefault(require("https"));
const COMMAND_TIMEOUT_IN_MILLISECS = moment_1.default.duration(10.5, "minutes").asMilliseconds();
const QUERY_TIMEOUT_IN_MILLISECS = moment_1.default.duration(4.5, "minutes").asMilliseconds();
const CLIENT_SERVER_DELTA_IN_MILLISECS = moment_1.default.duration(0.5, "minutes").asMilliseconds();
const MGMT_PREFIX = ".";
var ExecutionType;
(function (ExecutionType) {
    ExecutionType["Mgmt"] = "mgmt";
    ExecutionType["Query"] = "query";
    ExecutionType["Ingest"] = "ingest";
    ExecutionType["QueryV1"] = "queryv1";
})(ExecutionType || (ExecutionType = {}));
class KustoClient {
    constructor(kcsb) {
        this.connectionString = typeof (kcsb) === "string" ? new connectionBuilder_1.default(kcsb) : kcsb;
        this.cluster = this.connectionString.dataSource;
        this.endpoints = {
            [ExecutionType.Mgmt]: `${this.cluster}/v1/rest/mgmt`,
            [ExecutionType.Query]: `${this.cluster}/v2/rest/query`,
            [ExecutionType.Ingest]: `${this.cluster}/v1/rest/ingest`,
            [ExecutionType.QueryV1]: `${this.cluster}/v1/rest/query`,
        };
        this.aadHelper = new security_1.default(this.connectionString);
        const headers = {
            "Accept": "application/json",
            "Accept-Encoding": "gzip,deflate",
            "x-ms-client-version": `Kusto.Node.Client:${package_json_1.default.version}`,
            "Connection": "Keep-Alive",
        };
        this.axiosInstance = axios_1.default.create({
            headers,
            validateStatus: (status) => status == 200,
            // keepAlive pools and reuses TCP connections, so it's faster
            httpAgent: new http_1.default.Agent({ keepAlive: true }),
            httpsAgent: new https_1.default.Agent({ keepAlive: true }),
        });
    }
    execute(db, query, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            query = query.trim();
            if (query.startsWith(MGMT_PREFIX)) {
                return this.executeMgmt(db, query, properties);
            }
            return this.executeQuery(db, query, properties);
        });
    }
    executeQuery(db, query, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._execute(this.endpoints[ExecutionType.Query], ExecutionType.Query, db, query, null, properties);
        });
    }
    executeQueryV1(db, query, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._execute(this.endpoints[ExecutionType.QueryV1], ExecutionType.QueryV1, db, query, null, properties);
        });
    }
    executeMgmt(db, query, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._execute(this.endpoints[ExecutionType.Mgmt], ExecutionType.Mgmt, db, query, null, properties);
        });
    }
    executeStreamingIngest(db, table, stream, streamFormat, mappingName) {
        return __awaiter(this, void 0, void 0, function* () {
            let endpoint = `${this.endpoints[ExecutionType.Ingest]}/${db}/${table}?streamFormat=${streamFormat}`;
            if (mappingName != null) {
                endpoint += `&mappingName=${mappingName}`;
            }
            return this._execute(endpoint, ExecutionType.Ingest, db, null, stream, null);
        });
    }
    _execute(endpoint, executionType, db, query, stream, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            const headers = {};
            let payload;
            let clientRequestPrefix = "";
            let clientRequestId;
            const timeout = this._getClientTimeout(executionType, properties);
            let payloadStr = "";
            if (query != null) {
                payload = {
                    "db": db,
                    "csl": query
                };
                if (properties != null) {
                    payload.properties = properties.toJson();
                    clientRequestId = properties.clientRequestId;
                    if (properties.application != null) {
                        headers["x-ms-app"] = properties.application;
                    }
                    if (properties.user != null) {
                        headers["x-ms-user"] = properties.user;
                    }
                }
                payloadStr = JSON.stringify(payload);
                headers["Content-Type"] = "application/json; charset=utf-8";
                clientRequestPrefix = "KNC.execute;";
            }
            else if (stream != null) {
                payloadStr = stream;
                clientRequestPrefix = "KNC.executeStreamingIngest;";
                headers["Content-Encoding"] = "gzip";
                headers["Content-Type"] = "multipart/form-data";
            }
            headers["x-ms-client-request-id"] = clientRequestId || clientRequestPrefix + `${uuid_1.v4()}`;
            headers.Authorization = yield this.aadHelper._getAuthHeader();
            return this._doRequest(endpoint, executionType, headers, payloadStr, timeout, properties);
        });
    }
    _doRequest(endpoint, executionType, headers, payload, timeout, properties) {
        return __awaiter(this, void 0, void 0, function* () {
            const axiosConfig = {
                headers,
                timeout,
            };
            let axiosResponse;
            try {
                axiosResponse = yield this.axiosInstance.post(endpoint, payload, axiosConfig);
            }
            catch (error) {
                if (error.response) {
                    throw error.response.data.error;
                }
                throw error;
            }
            return this._parseResponse(axiosResponse.data, executionType, properties, axiosResponse.status);
        });
    }
    _parseResponse(response, executionType, properties, status) {
        const { raw } = properties || {};
        if (raw === true || executionType == ExecutionType.Ingest) {
            return response;
        }
        let kustoResponse = null;
        try {
            if (executionType == ExecutionType.Query) {
                kustoResponse = new response_1.KustoResponseDataSetV2(response);
            }
            else {
                kustoResponse = new response_1.KustoResponseDataSetV1(response);
            }
        }
        catch (ex) {
            throw new Error(`Failed to parse response ({${status}}) with the following error [${ex}].`);
        }
        if (kustoResponse.getErrorsCount().errors > 0) {
            throw new Error(`Kusto request had errors. ${kustoResponse.getExceptions()}`);
        }
        return kustoResponse;
    }
    _getClientTimeout(executionType, properties) {
        if (properties != null) {
            const clientTimeout = properties.getClientTimeout();
            if (clientTimeout) {
                return clientTimeout;
            }
            const serverTimeout = properties.getTimeout();
            if (serverTimeout) {
                return serverTimeout + CLIENT_SERVER_DELTA_IN_MILLISECS;
            }
        }
        return (executionType == ExecutionType.Query || executionType == ExecutionType.QueryV1) ? QUERY_TIMEOUT_IN_MILLISECS : COMMAND_TIMEOUT_IN_MILLISECS;
    }
}
exports.KustoClient = KustoClient;
exports.default = KustoClient;
//# sourceMappingURL=client.js.map